import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Crown, Loader2, CheckCircle2, XCircle } from "lucide-react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSeoTitle } from "@/lib/useSeoTitle";

export default function TipSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", payment_status: null, package_id: null });
  const attempts = useRef(0);

  useSeoTitle({
    title: "Payment status | TalesTalesTales",
    description: "Confirming your TalesTalesTales payment.",
    path: "/pricing/success",
    noindex: true,
  });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session id." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        // 1) Poll the session-scoped status endpoint (server-side entitlement gate).
        const r = await api.get(`/payments/status/${sessionId}`);
        // Session may report payment_status="paid" while Stripe is still holding
        // the payment for review — trust `billing_state`, not payment_status.
        const bill = r.data.billing_state;
        if (r.data.status === "expired" || bill === "expired") {
          setState({ phase: "expired" });
          return;
        }
        if (bill === "payment_failed" || bill === "canceled") {
          setState({ phase: "blocked", reason: bill });
          return;
        }
        if (r.data.requires_action) {
          setState({ phase: "action_required" });
          return;
        }
        if (bill === "active" || bill === "trialing" || r.data.payment_status === "paid") {
          // 2) Cross-check the canonical billing endpoint — never trust
          //    session data alone; a payment blocked between checkout
          //    and now must still fail the check.
          try {
            const b = await api.get("/billing/status");
            if (b.data.grant_tier) {
              setState({ phase: "paid", payment_status: "paid",
                         package_id: r.data.package_id });
              return;
            }
            // Server says entitlement not active — treat as blocked.
            setState({ phase: "blocked", reason: b.data.subscription_status });
            return;
          } catch {
            // Fall through and keep polling on transient error.
          }
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ phase: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState({ phase: "error", message: "Could not verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="tip-success-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div className="max-w-xl mx-auto px-6 py-24">
        <div className="parchment gold-border rounded-2xl p-10 text-center candle-glow rise">
          {state.phase === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2">Counting the gold...</h2>
              <p className="font-body text-amber-100/70">The vault confirms your offering.</p>
            </>
          )}
          {state.phase === "paid" && (
            <>
              <Crown className="w-12 h-12 text-amber-300 mx-auto mb-4 flicker" />
              <div className="font-ui text-xs uppercase tracking-[0.4em] text-amber-300/80 mb-3">Welcome, Patron</div>
              <h2 className="font-display text-4xl text-amber-50 mb-3" data-testid="tip-success-title">
                The bards salute you
              </h2>
              <p className="font-body text-amber-100/75 leading-relaxed mb-8">
                Your <span className="gold-text">{state.package_id}</span> offering has been accepted.
                Your name is etched in the patron scroll.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  data-testid="tip-success-dashboard-btn"
                  className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12"
                  onClick={() => nav("/dashboard")}
                >
                  Return to your saga
                </Button>
              </div>
            </>
          )}
          {state.phase === "expired" && (
            <>
              <XCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2">The offering expired</h2>
              <p className="font-body text-amber-100/70 mb-6">No coin changed hands. Try again when you wish.</p>
              <Link to="/pricing"><Button className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12">Return to patron scroll</Button></Link>
            </>
          )}
          {state.phase === "blocked" && (
            <>
              <XCircle data-testid="tip-blocked-icon" className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2" data-testid="tip-blocked-title">
                Payment could not be completed
              </h2>
              <p className="font-body text-amber-100/70 mb-6">
                Your bank or card issuer blocked this transaction, so no tier
                has been granted. Please try a different card or contact your
                bank, then try again.
              </p>
              <Link to="/pricing"><Button className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12">Try again</Button></Link>
            </>
          )}
          {state.phase === "action_required" && (
            <>
              <Loader2 className="w-10 h-10 text-amber-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2" data-testid="tip-action-required-title">
                Your bank needs a moment
              </h2>
              <p className="font-body text-amber-100/70 mb-6">
                Additional authentication (like 3D Secure) is required. Complete
                the verification in the popup your bank sent, then come back and refresh.
              </p>
              <Button
                data-testid="tip-action-refresh-btn"
                onClick={() => window.location.reload()}
                className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12"
              >
                I&apos;ve verified — refresh
              </Button>
            </>
          )}
          {state.phase === "timeout" && (
            <>
              <Loader2 className="w-10 h-10 text-amber-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2">Still verifying...</h2>
              <p className="font-body text-amber-100/70 mb-6">
                Your bank may be slow. Refresh in a moment, or check your dashboard.
              </p>
              <Button onClick={() => window.location.reload()} className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12">
                Refresh
              </Button>
            </>
          )}
          {state.phase === "error" && (
            <>
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-amber-50 mb-2">A scribe&apos;s error</h2>
              <p className="font-body text-amber-100/70 mb-6">{state.message}</p>
              <Link to="/pricing"><Button className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12">Back to patron scroll</Button></Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
